import React, { useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import type { EvaluationItem, BlankDef } from '../../../../../types/course';
import FloatingLabelPill from '../shared/FloatingLabelPill';
import SectionHeader from '../shared/SectionHeader';
import TrashButton from '../shared/TrashButton';
import { uid } from '../shared/tokens';

interface ItemEditorProps {
  item: Extract<EvaluationItem, { type: 'completion_fill_blank' }>;
  onChange: (patch: Partial<Extract<EvaluationItem, { type: 'completion_fill_blank' }>>) => void;
  onDelete: () => void;
  readOnly?: boolean;
}

type Segment = { kind: 'text'; value: string } | { kind: 'blank'; answer: string };

const BLANK_RE = /\[\[([^\]]+)\]\]/g;

function parsePrompt(prompt: string): { segments: Segment[]; answers: string[] } {
  const segments: Segment[] = [];
  const answers: string[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  const re = new RegExp(BLANK_RE.source, 'g');
  while ((match = re.exec(prompt)) !== null) {
    if (match.index > lastIndex) {
      segments.push({ kind: 'text', value: prompt.slice(lastIndex, match.index) });
    }
    const answer = match[1].trim();
    segments.push({ kind: 'blank', answer });
    answers.push(answer);
    lastIndex = re.lastIndex;
  }
  if (lastIndex < prompt.length) {
    segments.push({ kind: 'text', value: prompt.slice(lastIndex) });
  }
  return { segments, answers };
}

const EASE_OUT: [number, number, number, number] = [0.23, 1, 0.32, 1];

export default function CompletionFillBlankItem({
  item,
  onChange,
  onDelete,
  readOnly,
}: ItemEditorProps) {
  const { prompt, blanks } = item.config;

  const { segments, answers } = useMemo(() => parsePrompt(prompt), [prompt]);

  const handlePromptChange = (nextPrompt: string) => {
    const { answers: detected } = parsePrompt(nextPrompt);
    const nextBlanks: BlankDef[] = detected.map((answer, idx) => {
      const existing = blanks[idx];
      return existing ? { ...existing, answer } : { id: uid('blank'), answer };
    });
    onChange({
      config: { ...item.config, prompt: nextPrompt, blanks: nextBlanks },
    });
  };

  const count = answers.length;
  const isPreviewEmpty =
    segments.length === 0 ||
    (segments.length === 1 && segments[0].kind === 'text' && segments[0].value.trim() === '');

  return (
    <div className="flex flex-col gap-[31px] w-full">
      <SectionHeader title="Completion" />

      <FloatingLabelPill
        label="Question"
        value={prompt}
        onChange={handlePromptChange}
        placeholder="The [[Pacific]] is the largest ocean on Earth"
        size="xl"
        readOnly={readOnly}
        multiline
        rows={3}
        trailingIcon={!readOnly && <TrashButton onClick={onDelete} ariaLabel="Delete question" />}
      />

      <p className="-mt-4 text-xs text-text-secondary font-sans text-center">
        Wrap the answer in double brackets to mark it as a blank:{' '}
        <code className="px-1.5 py-0.5 rounded-md bg-primary-500/10 text-primary-500 font-mono text-[11px]">
          [[answer]]
        </code>
      </p>

      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between">
          <span className="text-xs font-sans font-semibold uppercase tracking-wider text-text-secondary">
            Preview
          </span>
          <AnimatePresence mode="popLayout">
            {count > 0 && (
              <motion.span
                key={count}
                initial={{ opacity: 0, y: -2, scale: 0.9 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 2, scale: 0.9 }}
                transition={{ duration: 0.18, ease: EASE_OUT }}
                className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-primary-500/10 text-primary-500 font-sans text-[11px] font-semibold"
              >
                {count} {count === 1 ? 'blank' : 'blanks'} detected
              </motion.span>
            )}
          </AnimatePresence>
        </div>
        <div className="min-h-[60px] px-4 py-3.5 rounded-2xl border border-text-secondary/50 bg-surface-primary font-sans text-[15px] leading-7 text-text-primary">
          {isPreviewEmpty ? (
            <span className="text-text-disabled italic">
              Your question preview appears here…
            </span>
          ) : (
            segments.map((seg, i) =>
              seg.kind === 'text' ? (
                <span key={i} className="whitespace-pre-wrap">
                  {seg.value}
                </span>
              ) : (
                <motion.span
                  key={`blank-${i}-${seg.answer}`}
                  initial={{ opacity: 0, scale: 0.92 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.2, ease: EASE_OUT }}
                  className="inline-flex items-center mx-0.5 px-2.5 py-[2px] rounded-full bg-primary-500/10 border border-primary-500/40 text-primary-500 font-sans font-semibold text-sm leading-6 align-baseline"
                >
                  {seg.answer || <span className="opacity-50 italic">blank</span>}
                </motion.span>
              ),
            )
          )}
        </div>
      </div>
    </div>
  );
}
